/**
 * 门店营收监控 - 搜索面板配置
 *
 * 提供搜索表单项与按钮的工厂函数，供主页面 CompsSearch 使用。
 * 支持大区/省/市/品牌/门店/状态等多维度筛选，地区间联动（上级变化时清空下级）。
 */

import type { SearchItemConfig } from "@ms/shared-components/components/GenericPanelSearch";
import type { BrandOption } from "../../../hooks/useBrandOptions";
import type { HotelOption } from "../../../hooks/useHotelOptions";

/**
 * 创建搜索表单项配置
 * @param params - 各类下拉选项（大区/省/市/品牌/门店），由 useAreaOptions / useBrandOptions / useHotelOptions 提供
 * @returns SearchItemConfig[] 供 CompsSearch 渲染
 */
export const createSearchItems = (params: {
  regionOptions?: { value: any; label: string }[];
  provinceOptions?: { value: any; label: string }[];
  cityOptions?: { value: any; label: string }[];
  hotelOptions?: HotelOption[];
  brandOptions?: BrandOption[];
} = {}): SearchItemConfig[] => {
  const {
    regionOptions = [],
    provinceOptions = [],
    cityOptions = [],
    hotelOptions = [],
    brandOptions = [],
  } = params;
  return [
    {
      name: "search",
      label: "搜索",
      type: "input",
      props: {
        placeholder: "请输入酒店名称或编码",
        allowClear: true,
      },
    },
    {
      name: "provinceId",
      label: "省",
      type: "select",
      options: provinceOptions,
      props: {
        placeholder: "请选择省",
        allowClear: true,
        showSearch: true,
        optionFilterProp: "label",
        mode: "multiple",
      },
    },
    {
      name: "cityId",
      label: "市",
      type: "select",
      options: cityOptions,
      props: {
        placeholder: "请选择市",
        allowClear: true,
        showSearch: true,
        optionFilterProp: "label",
        mode: "multiple",
      },
    },

    {
      name: "regionId",
      label: "大区",
      type: "select",
      options: regionOptions,
      props: {
        placeholder: "请选择大区",
        allowClear: true,
        showSearch: true,
        optionFilterProp: "label",
        mode: "multiple", // 支持多选
      },
    },
    {
      name: "hotelId",
      label: "门店",
      type: "select",
      options: hotelOptions,
      props: {
        placeholder: "请选择门店",
        allowClear: true,
        showSearch: true,
        optionFilterProp: "label",
        mode: "multiple", // 支持多选
      },
    },
    {
      name: "brandIds",
      label: "品牌",
      type: "select",
      options: brandOptions,
      props: {
        placeholder: "请选择品牌",
        allowClear: true,
        showSearch: true,
        optionFilterProp: "label",
        mode: "multiple",
      },
    },
    {
      name: "status",
      label: "状态",
      type: "select",
      options: [
        // 与 pms_hotel_cache.status 对齐：0=已上线, 1=已下线, 2=待上线
        { value: 0, label: "已上线" },
        { value: 1, label: "已下线" },
        { value: 2, label: "待上线" },
      ],
      props: {
        placeholder: "请选择状态",
        allowClear: true,
      },
    },
    {
      name: "showTestStore",
      label: "显示测试店",
      type: "switch",
      props: {
        checkedChildren: "显示",
        unCheckedChildren: "隐藏",
      },
    },
  ];
};

/**
 * 搜索区按钮配置
 * 注意：icon 为图标名称字符串（如 "SearchOutlined"），需在 index 中通过 getIcon() 转为 React 节点
 */
export interface SearchButtonConfig {
  key: string;
  iconName?: string; // 图标名称，如 "SearchOutlined", "ReloadOutlined"
  text: string;
  type?: "primary" | "default" | "dashed" | "link" | "text";
  onClick: () => void;
  group?: string;
}

export const createSearchButtons = (handlers: {
  handleSearchClick: () => void;
  handleReset: () => void;
}): SearchButtonConfig[] => [
    {
      key: "reset",
      iconName: "ReloadOutlined",
      text: "重置",
      onClick: handlers.handleReset,
      group: "primary",
    },
    {
      key: "search",
      text: "搜索",
      iconName: "SearchOutlined",
      type: "primary",
      onClick: handlers.handleSearchClick,
      group: "primary",
    },
  ];

/** 搜索面板通用配置 */
export const SEARCH_PANEL_CONFIG = {
  // 搜索面板标题
  SEARCH_TITLE: "搜索",
  // 列数配置
  DEFAULT_COLUMNS: 4,
  // 标签宽度
  LABEL_WIDTH: 80,
  // 是否可折叠
  COLLAPSIBLE: true,
  // 默认折叠状态
  DEFAULT_COLLAPSED: false,
} as const;
